import { useCallback, useContext, useMemo, useState } from 'react';
import { ArchiveSwitch } from '@/modules/common/components/ArchiveSwitch.tsx';
import { ModalBrokerAddUpdate } from '@/modules/issuer-dashboard/components/portfolios/ModalBrokerAddUpdate.tsx';
import {
  useIssuerDeletePortfolioBrokerMutation,
  useIssuerPortfolioBrokersInfiniteListQuery,
  useIssuerRestorePortfolioBrokerMutation,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import {
  BodyCell,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuItem,
  ModalProviderContext,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { DotsHorizontalIcon } from 'ui/icons';
import { goodFormatNumberCustom } from 'ui/utils';
import { BrokerExtended } from 'api';

type BrokerTable = 'Broker name' | 'Balances' | 'Connection Email' | '';

const tableHeadItems: TableHeadColumn<BrokerTable>[] = [
  { title: 'Broker name' },
  { title: 'Balances' },
  // { title: 'Connection Email' },
  { title: '', className: 'w-10 max-w-10' },
];

interface PortfolioBrokersTabProps {
  portfolioId: number;
}

export const PortfolioBrokersTab = ({ portfolioId }: PortfolioBrokersTabProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { isMobile } = useIsMobile();
  const [archived, setArchived] = useState(false);
  const {
    fetchNextPage,
    isLoading,
    hasNextPage,
    isError,
    data: dataBrokersList,
  } = useIssuerPortfolioBrokersInfiniteListQuery({
    page: 1,
    page_size: 10,
    portfolio_id: portfolioId,
    archived,
  });

  const tableItems = useMemo<TableBodyColumn[]>(() => {
    if (!dataBrokersList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    dataBrokersList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(item => {
          const { name, balances } = item;

          const body: BodyCell[] = [
            {
              cell: <div className="text-blue-700">{name}</div>,
            },
            {
              cell: (
                <span>
                  {balances
                    ?.map(({ currency, value }) =>
                      goodFormatNumberCustom(value, currency.toUpperCase(), 0, 2),
                    )
                    .join(', ') || '-'}
                </span>
              ),
            },
            {
              cell: <BrokerMoreButton item={item} portfolioId={portfolioId} archived={archived} />,
            },
          ];

          return {
            cells: body,
          };
        }),
      );
    });
    return result;
  }, [archived, dataBrokersList, portfolioId]);

  const handleOpenAddBrokerModal = useCallback(() => {
    openModal(ModalBrokerAddUpdate, {
      portfolioId,
    });
  }, [openModal, portfolioId]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start justify-end gap-4 w-full px-3 sm:px-6">
        <div className="flex items-center gap-4 flex-wrap justify-end">
          <ArchiveSwitch active={archived} onChange={setArchived} />
          <Button className="w-[144px] px-0" onClick={handleOpenAddBrokerModal} disabled={archived}>
            Add broker
          </Button>
        </div>
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={isMobile}
        withBorder={true}
        withBorderX={false}
        headItems={tableHeadItems}
        items={tableItems}
        isLoading={isLoading}
        isError={isError}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

export interface BankMoreButtonProps {
  item: BrokerExtended;
  portfolioId: number;
  archived?: boolean;
}

export const BrokerMoreButton = ({ item, portfolioId, archived = false }: BankMoreButtonProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: mutateAsyncDelete } = useIssuerDeletePortfolioBrokerMutation();
  const { mutateAsync: mutateAsyncRestore } = useIssuerRestorePortfolioBrokerMutation();
  const items = useMemo<DropdownMenuItem[]>(() => {
    if (archived) {
      return [
        {
          label: 'Restore',
          onClick: () => {
            openModal(ConfirmModal, {
              title: <span>Restore {item.name}</span>,
              onConfirm: async () => {
                await mutateAsyncRestore(item.id);
              },
            });
          },
        },
      ];
    }
    return [
      {
        label: 'Edit',
        onClick: () => {
          openModal(ModalBrokerAddUpdate, { item, portfolioId });
        },
      },
      {
        label: 'Archive',
        onClick: () => {
          openModal(ConfirmModal, {
            title: <span>Archive {item.name}</span>,
            content: 'This action is irreversible.',
            onConfirm: async () => {
              await mutateAsyncDelete(item.id);
            },
          });
        },
      },
    ];
  }, [archived, item, mutateAsyncDelete, mutateAsyncRestore, openModal, portfolioId]);

  return (
    <div>
      <DropdownMenu
        items={items}
        button={
          <button className="flex items-center justify-center">
            <img
              alt="More..."
              src={DotsHorizontalIcon}
              className="cursor-pointer hover:opacity-70 min-w-[24px] min-h-[24px]"
            />
          </button>
        }
        placement="left-start"
        autoPlace
        className="w-[180px]"
        containerClassName="max-w-6"
      />
    </div>
  );
};
